import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
  TextField,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
  Send as SendIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Flag as FlagIcon,
  Delete as DeleteIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../config/api';
import SEOHead from '../components/SEOHead';

const ForumPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newComment, setNewComment] = useState('');
  const [commentAnonymous, setCommentAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [commentError, setCommentError] = useState('');

  const token = localStorage.getItem('token');

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState({ type: null, id: null });
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  
  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Menu anchor for post actions
  const [postMenuAnchor, setPostMenuAnchor] = useState(null);
  const [commentMenuAnchor, setCommentMenuAnchor] = useState(null);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: null, id: null });

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/forum/posts/${slug}`);
        setPost(response.data.post);
        setComments(response.data.comments);
        setRelatedPosts(response.data.relatedPosts);
      } catch (err) {
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) return;
      try {
        const response = await api.get('/forum/admin/check');
        setIsAdmin(response.data.isAdmin);
      } catch (err) {
        // Not admin or error
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [token]);

  // Report handlers
  const handleOpenReportDialog = (type, id) => {
    if (!token) {
      setSnackbar({ open: true, message: 'Please log in to report content', severity: 'warning' });
      return;
    }
    setReportTarget({ type, id });
    setReportReason('');
    setReportDescription('');
    setReportDialogOpen(true);
    setPostMenuAnchor(null);
    setCommentMenuAnchor(null);
  };

  const handleSubmitReport = async () => {
    if (!reportReason) {
      setSnackbar({ open: true, message: 'Please select a reason', severity: 'error' });
      return;
    }

    setReportSubmitting(true);
    try {
      await api.post('/forum/report', {
        post_id: reportTarget.type === 'post' ? reportTarget.id : null,
        comment_id: reportTarget.type === 'comment' ? reportTarget.id : null,
        reason: reportReason,
        description: reportDescription,
      });
      setSnackbar({ open: true, message: 'Report submitted successfully. Thank you for helping keep our community safe.', severity: 'success' });
      setReportDialogOpen(false);
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Failed to submit report', 
        severity: 'error' 
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  // Admin delete handlers
  const handleOpenDeleteDialog = (type, id) => {
    setDeleteTarget({ type, id });
    setDeleteDialogOpen(true);
    setPostMenuAnchor(null);
    setCommentMenuAnchor(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteTarget.type === 'post') {
        await api.delete(`/forum/admin/posts/${deleteTarget.id}`);
        setSnackbar({ open: true, message: 'Post deleted successfully', severity: 'success' });
        navigate('/forum');
      } else if (deleteTarget.type === 'comment') {
        await api.delete(`/forum/admin/comments/${deleteTarget.id}`);
        setSnackbar({ open: true, message: 'Comment deleted successfully', severity: 'success' });
        // Refresh comments
        const response = await api.get(`/forum/posts/${slug}`);
        setComments(response.data.comments);
      }
      setDeleteDialogOpen(false);
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Failed to delete', 
        severity: 'error' 
      });
    }
  };

  const handleHideComment = async (commentId) => {
    try {
      await api.patch(`/forum/admin/comments/${commentId}/hide`);
      setSnackbar({ open: true, message: 'Comment hidden successfully', severity: 'success' });
      const response = await api.get(`/forum/posts/${slug}`);
      setComments(response.data.comments);
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Failed to hide comment', 
        severity: 'error' 
      });
    }
    setCommentMenuAnchor(null);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    if (!token) {
      setCommentError('Please log in to comment');
      return;
    }

    setSubmitting(true);
    setCommentError('');

    try {
      await api.post(`/forum/posts/${post.id}/comments`, { content: newComment, is_anonymous: commentAnonymous });
      // Refresh comments
      const response = await api.get(`/forum/posts/${slug}`);
      setComments(response.data.comments);
      setNewComment('');
      setCommentAnonymous(false);
    } catch (err) {
      // Show detailed validation errors if available
      if (err.response?.data?.details?.length > 0) {
        setCommentError(err.response.data.details.join('. '));
      } else if (err.response?.data?.error?.includes('Security')) {
        setCommentError('Security verification failed. Please refresh and try again.');
      } else {
        setCommentError(err.response?.data?.error || 'Failed to post comment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h5" color="error">{error || 'Post not found'}</Typography>
        <Button onClick={() => navigate('/forum')} sx={{ mt: 2 }}>
          Back to Community
        </Button>
      </Container>
    );
  }

  const parseTags = () => {
    try {
      return JSON.parse(post.tags) || [];
    } catch {
      return [];
    }
  };

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "DiscussionForumPosting",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://dejavunomad.com/forum/${post.slug}`
      },
      "headline": post.title,
      "text": post.excerpt || post.content?.substring(0, 500) || post.title,
      "url": `https://dejavunomad.com/forum/${post.slug}`,
      "author": {
        "@type": "Person",
        "name": post.author_name || post.partner_name || "Anonymous",
        "url": `https://dejavunomad.com/forum`
      },
      "datePublished": post.created_at,
      "dateModified": post.updated_at || post.created_at,
      "publisher": {
        "@type": "Organization",
        "name": "Déjà Vu Nomad",
        "url": "https://dejavunomad.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://dejavunomad.com/logo192.png"
        }
      },
      "interactionStatistic": [
        {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/ViewAction",
          "userInteractionCount": post.view_count || 0
        },
        {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/CommentAction",
          "userInteractionCount": comments.length
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://dejavunomad.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Community",
          "item": "https://dejavunomad.com/forum"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": post.title,
          "item": `https://dejavunomad.com/forum/${post.slug}`
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title={`${post.title} | Travel Community - Déjà Vu Nomad`}
        description={post.excerpt}
        keywords={parseTags().join(', ')}
        canonical={`https://dejavunomad.com/forum/${post.slug}`}
        structuredData={structuredData}
      />

      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/forum" underline="hover" color="inherit">
            Forum
          </Link>
          <Link 
            component={RouterLink} 
            to={`/forum?country=${post.category_slug}`} 
            underline="hover" 
            color="inherit"
          >
            {post.flag_emoji} {post.category_name}
          </Link>
          <Typography color="text.primary" sx={{ 
            maxWidth: 300, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {post.title}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Main Content */}
          <Box sx={{ flex: 1 }}>
            {/* Post Header */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    label={`${post.flag_emoji} ${post.category_name}`}
                    sx={{ backgroundColor: '#8B1538', color: 'white' }}
                  />
                  <Chip label={post.category_region} variant="outlined" />
                  {post.is_featured ? <Chip label="⭐ Featured" color="warning" /> : null}
                </Stack>

                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                  {post.title}
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: '#2e7d6c' }}>
                    {(post.author_name || post.partner_name || 'A').charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2">
                      {post.author_name || post.partner_name || 'Anonymous'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(post.created_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <ViewIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {post.view_count?.toLocaleString()} views
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<ShareIcon />}
                    onClick={handleShare}
                  >
                    Share
                  </Button>
                  <Tooltip title="More options">
                    <IconButton 
                      size="small" 
                      onClick={(e) => setPostMenuAnchor(e.currentTarget)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={postMenuAnchor}
                    open={Boolean(postMenuAnchor)}
                    onClose={() => setPostMenuAnchor(null)}
                  >
                    <MenuItem onClick={() => handleOpenReportDialog('post', post.id)}>
                      <FlagIcon sx={{ mr: 1, fontSize: 18 }} /> Report Post
                    </MenuItem>
                    {isAdmin && (
                      <MenuItem 
                        onClick={() => handleOpenDeleteDialog('post', post.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete Post
                      </MenuItem>
                    )}
                  </Menu>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {/* Post Content */}
                <Box
                  sx={{
                    '& h3': { mt: 3, mb: 1, fontWeight: 600 },
                    '& p': { mb: 2, lineHeight: 1.8 },
                    '& ul, & ol': { mb: 2, pl: 3 },
                    '& li': { mb: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Tags */}
                {parseTags().length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Tags:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {parseTags().map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          onClick={() => navigate(`/forum?search=${tag}`)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Comments Section */}
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  💬 Comments ({comments.length})
                </Typography>

                {/* Comment Form */}
                <Box component="form" onSubmit={handleSubmitComment} sx={{ mb: 4 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={token ? "Share your thoughts or ask a question..." : "Please log in to comment"}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    disabled={!token || submitting}
                    sx={{ mb: 2 }}
                  />
                  {commentError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{commentError}</Alert>
                  )}
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!token || submitting || !newComment.trim()}
                      startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
                      sx={{ backgroundColor: '#8B1538', '&:hover': { backgroundColor: '#6d1029' } }}
                    >
                      Post Comment
                    </Button>
                    {token && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={commentAnonymous}
                            onChange={(e) => setCommentAnonymous(e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">Post anonymously</Typography>}
                      />
                    )}
                    {!token && (
                      <Button variant="outlined" onClick={() => navigate('/login')}>
                        Log In
                      </Button>
                    )}
                  </Stack>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Comments List */}
                {comments.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No comments yet. Be the first to share your thoughts!
                  </Typography>
                ) : (
                  <Stack spacing={3}>
                    {comments.map((comment) => (
                      <Box key={comment.id} sx={{ display: 'flex', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#f0f0f0', color: '#333', width: 40, height: 40 }}>
                          {(comment.author_name || comment.partner_name || 'A').charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2">
                              {comment.author_name || comment.partner_name || 'Anonymous'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(comment.created_at)}
                            </Typography>
                            <Box sx={{ ml: 'auto' }}>
                              <Tooltip title="More options">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    setSelectedCommentId(comment.id);
                                    setCommentMenuAnchor(e.currentTarget);
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Stack>
                          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            {comment.content}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {/* Comment action menu */}
                    <Menu
                      anchorEl={commentMenuAnchor}
                      open={Boolean(commentMenuAnchor)}
                      onClose={() => setCommentMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => handleOpenReportDialog('comment', selectedCommentId)}>
                        <FlagIcon sx={{ mr: 1, fontSize: 18 }} /> Report Comment
                      </MenuItem>
                      {isAdmin && [
                        <MenuItem 
                          key="hide"
                          onClick={() => handleHideComment(selectedCommentId)}
                        >
                          <HideIcon sx={{ mr: 1, fontSize: 18 }} /> Hide Comment
                        </MenuItem>,
                        <MenuItem 
                          key="delete"
                          onClick={() => handleOpenDeleteDialog('comment', selectedCommentId)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete Comment
                        </MenuItem>
                      ]}
                    </Menu>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Sidebar */}
          <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/forum')}
              sx={{ mb: 3 }}
            >
              Back to Community
            </Button>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    📚 Related Discussions
                  </Typography>
                  <Stack spacing={2}>
                    {relatedPosts.map((related) => (
                      <Box
                        key={related.id}
                        onClick={() => navigate(`/forum/${related.slug}`)}
                        sx={{
                          cursor: 'pointer',
                          p: 1.5,
                          borderRadius: 1,
                          '&:hover': { backgroundColor: '#f5f5f5' },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {related.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(related.created_at)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </Container>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagIcon color="warning" />
            Report {reportTarget.type === 'post' ? 'Post' : 'Comment'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help us keep the community safe by reporting inappropriate content. 
            Reports are sent to our moderation team for review.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason *</InputLabel>
            <Select
              value={reportReason}
              label="Reason *"
              onChange={(e) => setReportReason(e.target.value)}
            >
              <MenuItem value="spam">Spam or advertising</MenuItem>
              <MenuItem value="harassment">Harassment or bullying</MenuItem>
              <MenuItem value="inappropriate">Inappropriate or offensive content</MenuItem>
              <MenuItem value="misinformation">Misinformation</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional details (optional)"
            placeholder="Provide any additional context that might help our moderators..."
            value={reportDescription}
            onChange={(e) => setReportDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleSubmitReport}
            disabled={reportSubmitting || !reportReason}
            startIcon={reportSubmitting ? <CircularProgress size={16} /> : <FlagIcon />}
          >
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <DeleteIcon />
            Delete {deleteTarget.type === 'post' ? 'Post' : 'Comment'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteTarget.type}? 
            {deleteTarget.type === 'post' && ' This will also delete all associated comments.'}
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleConfirmDelete}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ForumPost;

